import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, IconButton, CircularProgress } from '@mui/material';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import { useHtmlToImage, useHtmlToPDF } from "../../hooks/dataHooks";
import { handleHtmlConversion } from "../../utils/commonUtils";

import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import CancelPresentationOutlinedIcon from '@mui/icons-material/CancelPresentationOutlined';

const QuillEditor = ({ mode = 'inline', open = false, onClose, onSave, content, setContent }) => {
    const { mutate: htmlToImage, isPending: htmlToImageLoading } = useHtmlToImage();
    const { mutate: htmlToPDF, isPending: htmlToPDFLoading } = useHtmlToPDF();

    const isModal = mode === 'popup';
    const isLoading = htmlToImageLoading || htmlToPDFLoading;

    const handleSave = () => {
        if (onSave) onSave(content);
        if (isModal && onClose) onClose();
    };

    const handleHTMLToImage = () => {
        handleHtmlConversion(htmlToImage, 'png', content);
    };

    const handleHTMLToPDF = () => {
        handleHtmlConversion(htmlToPDF, 'pdf', content);
    };

    const renderEditor = () => (
        <ReactQuill
            value={content}
            onChange={setContent}
            theme="snow"
            style={{ width: '100%' }}
            modules={{
                toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['link', 'image', 'code-block'],
                    ['clean'],
                ],
            }}
        />
    );

    return isModal ? (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            {/* Loading overlay */}
            {isLoading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.7)', // Semi-transparent overlay
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1300, // Ensure it's above other content
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            <DialogTitle>Edit Content</DialogTitle>
            <DialogContent>{renderEditor()}</DialogContent>
            <DialogActions
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                {/* Cancel Icon */}
                <IconButton
                    onClick={onClose}
                    disabled={isLoading}
                    color="secondary"
                    title="Cancel"
                    sx={{
                        borderRadius: '50%', // Ensure circular shape
                        border: '1px solid', // Optional: adds a border for better visibility
                        padding: 1, // Adjust padding for better circular appearance
                    }}
                >
                    <CancelPresentationOutlinedIcon />
                </IconButton>

                {/* Save as Image Icon */}
                <IconButton
                    onClick={handleHTMLToImage}
                    disabled={isLoading}
                    color="primary"
                    title="Save as Image"
                    sx={{
                        borderRadius: '50%',
                        border: '1px solid', // Optional border
                        padding: 1,
                    }}
                >
                    <CollectionsOutlinedIcon />
                </IconButton>

                {/* Save as PDF Icon */}
                <IconButton
                    onClick={handleHTMLToPDF}
                    disabled={isLoading}
                    color="primary"
                    title="Save as PDF"
                    sx={{
                        borderRadius: '50%',
                        border: '1px solid',
                        padding: 1,
                    }}
                >
                    <PictureAsPdfOutlinedIcon />
                </IconButton>
            </DialogActions>
        </Dialog>
    ) : (
        <Box>{renderEditor()}</Box>
    );
};

export default QuillEditor;
